/**
 * @fileoverview ContactChatSheet — wires the generic `ChatSheet` to the
 * contact-scoped chat backend.
 *
 * Mirrors `SummaryChatSheet` but threads on `(contactId, userId)` and
 * sends through `api.contacts.chat.sendContactChatMessage`. The agent
 * has the contact's overview, source links, and chronological notes
 * baked into its system prompt, so the suggested prompts in the empty
 * state revolve around that surface.
 *
 * Used by:
 * - src/components/jobs/phases/contacts/ContactDetailPanel.tsx
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { convexQuery, useConvexAction } from "@convex-dev/react-query";
import { useThreadMessages } from "@convex-dev/agent/react";
import { toast } from "sonner";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import {
  ChatSheet,
  type ChatMessageData,
  type ChatUserInfo,
} from "@/ui/composed/chat";

interface ContactChatSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: Id<"jobContacts">;
  contactName: string;
}

export function ContactChatSheet({
  open,
  onOpenChange,
  contactId,
  contactName,
}: ContactChatSheetProps) {
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [isNewThread, setIsNewThread] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);

  // Reset thread state whenever the panel switches contacts so we don't
  // carry a stale thread from contact A across to contact B.
  useEffect(() => {
    setActiveThreadId(null);
    setIsNewThread(false);
    setPendingMessage(null);
    setWaitingForResponse(false);
  }, [contactId]);

  // Current user — for the avatar in the chat input and message bubbles.
  const { data: currentUser } = useQuery(
    convexQuery(api.users.queries.getCurrentUser, {}),
  );
  const userInfo = useMemo<ChatUserInfo | undefined>(() => {
    if (!currentUser) return undefined;
    return {
      avatarUrl: currentUser.avatarUrl,
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
    };
  }, [currentUser]);

  // List threads for this (contact × user). Newest first.
  const { data: threadList } = useQuery(
    convexQuery(api.contacts.chatInternal.listContactChatThreads, {
      contactId,
    }),
  );

  // Auto-select the most recent thread on open unless the user
  // explicitly clicked "New".
  useEffect(() => {
    if (
      !activeThreadId &&
      !isNewThread &&
      threadList &&
      threadList.length > 0
    ) {
      setActiveThreadId(threadList[0].threadId);
    }
  }, [activeThreadId, isNewThread, threadList]);

  // Subscribe to messages on the active thread (with streaming deltas).
  const threadMessages = useThreadMessages(
    api.contacts.chatInternal.listContactChatMessages,
    activeThreadId ? { threadId: activeThreadId } : "skip",
    { initialNumItems: 50, stream: true },
  );

  type RawAgentMessage = {
    _id?: string;
    _creationTime: number;
    key?: string;
    streaming?: boolean;
    message?: {
      role?: string;
      content?:
        string | Array<{ type?: string; text?: string; toolName?: string }>;
    };
  };

  const realMessages: ChatMessageData[] = (
    (threadMessages?.results ?? []) as Array<RawAgentMessage>
  )
    .map((m): ChatMessageData | null => {
      const role = m.message?.role;
      if (role !== "user" && role !== "assistant") return null;
      const content = m.message?.content;
      if (!content) return null;

      const text =
        typeof content === "string"
          ? content.trim().length > 0
            ? content
            : null
          : Array.isArray(content) &&
              content.some((part) => part.type === "text" && part.text?.trim())
            ? content
                .filter((p) => p.type === "text")
                .map((p) => p.text ?? "")
                .join("")
            : null;

      if (text === null) return null;

      return {
        id: m.key ?? m._id ?? String(m._creationTime),
        role,
        content: text,
        createdAt: m._creationTime,
        isStreaming: m.streaming === true,
      };
    })
    .filter((m): m is ChatMessageData => m !== null);

  // Once the optimistic user message arrives in the real stream, drop it.
  useEffect(() => {
    if (
      pendingMessage &&
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      setPendingMessage(null);
    }
  }, [realMessages, pendingMessage]);

  const messages: ChatMessageData[] = useMemo(() => {
    if (!pendingMessage) return realMessages;
    if (
      realMessages.some(
        (m) => m.role === "user" && m.content === pendingMessage,
      )
    ) {
      return realMessages;
    }
    return [
      ...realMessages,
      {
        id: "optimistic-pending",
        role: "user" as const,
        content: pendingMessage,
        createdAt: Date.now(),
      },
    ];
  }, [realMessages, pendingMessage]);

  // Derive thinking + tool-call state from raw messages — same logic as
  // SummaryChatSheet so the typing indicator behaves identically.
  const { isAgentThinking, activeToolCalls } = useMemo(() => {
    const raw = (threadMessages?.results ?? []) as Array<RawAgentMessage>;
    if (raw.length === 0) {
      return {
        isAgentThinking: waitingForResponse,
        activeToolCalls: [] as string[],
      };
    }

    const tools: string[] = [];
    for (let i = raw.length - 1; i >= 0; i--) {
      const m = raw[i];
      if (m.message?.role === "user") break;
      if (m.message?.role === "assistant" && Array.isArray(m.message.content)) {
        for (const part of m.message.content) {
          const tc = part as { type?: string; toolName?: string };
          if (tc.type === "tool-call" && tc.toolName) {
            tools.push(tc.toolName);
          }
        }
      }
    }

    const lastRaw = raw[raw.length - 1];
    const lastRole = lastRaw.message?.role;

    if (lastRaw.streaming && lastRole === "assistant") {
      return { isAgentThinking: false, activeToolCalls: [] as string[] };
    }

    const hasToolCallParts =
      lastRole === "assistant" &&
      Array.isArray(lastRaw.message?.content) &&
      (lastRaw.message?.content as Array<{ type?: string }>).some(
        (p) => p.type === "tool-call",
      );

    const isWorking =
      waitingForResponse || lastRole === "tool" || hasToolCallParts;

    return {
      isAgentThinking: isWorking,
      activeToolCalls: isWorking ? tools : [],
    };
  }, [threadMessages?.results, waitingForResponse]);

  // Clear waitingForResponse once the agent reports it isn't thinking
  // anymore (otherwise it would stay sticky across conversations).
  useEffect(() => {
    if (waitingForResponse && !isAgentThinking) {
      setWaitingForResponse(false);
    }
  }, [waitingForResponse, isAgentThinking]);

  // Send a message — fires the public action which schedules streaming
  // on a thread it creates / reuses.
  const { mutateAsync: sendAction } = useMutation({
    mutationFn: useConvexAction(api.contacts.chat.sendContactChatMessage),
  });

  const handleSend = useCallback(
    async (message: string) => {
      setPendingMessage(message);
      setWaitingForResponse(true);
      setIsSending(true);
      try {
        const result = await sendAction({
          contactId,
          message,
          threadId: activeThreadId ?? undefined,
        });
        if (!activeThreadId) {
          setActiveThreadId(result.threadId);
          setIsNewThread(false);
        }
      } catch (err) {
        const friendly =
          err instanceof Error
            ? err.message
            : "Could not send your message \u2014 please try again.";
        console.error("[ContactChatSheet] Failed to send message:", err);
        setPendingMessage(null);
        setWaitingForResponse(false);
        toast.error(friendly);
      } finally {
        setTimeout(() => setIsSending(false), 500);
      }
    },
    [sendAction, contactId, activeThreadId],
  );

  const handleNewThread = useCallback(() => {
    setActiveThreadId(null);
    setIsNewThread(true);
  }, []);

  const handleSelectThread = useCallback((threadId: string) => {
    setActiveThreadId(threadId);
    setIsNewThread(false);
  }, []);

  const threads = useMemo(
    () =>
      (threadList ?? []).map((t) => ({
        threadId: t.threadId,
        createdAt: t.createdAt,
        updatedAt: t.updatedAt,
        preview: t.preview,
      })),
    [threadList],
  );

  const emptyMessage =
    `Ask Morgan anything about ${contactName}.\n\n` +
    "Try:\n" +
    "• \u201CSummarise everything we know about this contact.\u201D\n" +
    "• \u201CWhat was the last thing we noted?\u201D\n" +
    "• \u201CWhat sources confirm this contact?\u201D";

  return (
    <ChatSheet
      open={open}
      onOpenChange={onOpenChange}
      title={`Chat \u2014 ${contactName}`}
      description="Ask Morgan about this contact's overview, sources, and notes."
      messages={messages}
      onSend={handleSend}
      userInfo={userInfo}
      isSending={isSending}
      isThinking={isAgentThinking}
      activeToolCalls={activeToolCalls}
      emptyMessage={emptyMessage}
      inputPlaceholder={`Ask about ${contactName}\u2026`}
      threads={threads}
      activeThreadId={activeThreadId}
      onNewThread={handleNewThread}
      onSelectThread={handleSelectThread}
    />
  );
}
